import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { UI2FileUploadField, IUI2FileUploadFieldProps } from '../ui2-file-upload-field/ui2-file-upload-field'
import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import {
  UI2PhotoCaptureFieldProps,
  UI2_PHOTO_CAPTURE_FIELD_DEFAULT_PROPS,
} from 'shared-libs/components/ui2/fields/ui2-photo-capture-field/ui2-photo-capture-field-props'

import './_ui2-photo-capture-field.scss'

const DEFAULT_EMPTY_MESSAGE_DW = 'Click to upload a photo'
const DEFAULT_EMPTY_MESSAGE_MW = 'Tap to take photo'
const DEFAULT_ADD_MORE_TEXT = 'Upload photo'
const DEFAULT_EMPTY_READ_ONLY_TEXT = 'No images available'
const DEFAULT_PREFILLED_EDIT_TEXT = 'Add/Remove Photos'

export interface IUI2PhotoCaptureFieldProps
  extends UI2PhotoCaptureFieldProps,
    Omit<IUI2FileUploadFieldProps, keyof UI2PhotoCaptureFieldProps> {}

export class UI2PhotoCaptureField extends UI2FileUploadField {
  public static defaultProps: Partial<IUI2PhotoCaptureFieldProps> = {
    ...UI2_PHOTO_CAPTURE_FIELD_DEFAULT_PROPS,
    accept: 'image/*',
    addMoreMessage: DEFAULT_ADD_MORE_TEXT,
    maxImageDimension: UI2FileUploadField.defaultProps.maxImageDimension,
    imageQuality: UI2FileUploadField.defaultProps.imageQuality,
  }

  public constructor(props: any) {
    super(props)
  }

  // Photo capture holds images, not files — the inherited
  // 'No files available' reads wrong under a photo field.
  protected defaultEmptyReadOnlyText(): string {
    return DEFAULT_EMPTY_READ_ONLY_TEXT
  }

  protected defaultPrefilledEditText(): string {
    return DEFAULT_PREFILLED_EDIT_TEXT
  }

  // In OCR mode, render a plain text input so the user can manually enter the
  // value that would normally come from OCR on the native app.
  public render() {
    const { mode } = this.props as IUI2PhotoCaptureFieldProps
    if (mode === 'ocr') {
      return this.renderOcrModeWithShell()
    }
    return super.render()
  }

  private renderOcrModeWithShell(): React.ReactNode {
    const { className, style } = this.props
    return (
      <UI2FieldShell
        {...(this.props as IUI2FieldShellProps)}
        className={classNames('ui2-photoCaptureField', className)}
        style={style}
        isValueEmpty={() => false}
        renderReadOnly={this.renderOcrReadOnly}
      >
        {this.renderOcrInput()}
      </UI2FieldShell>
    )
  }

  private renderOcrReadOnly = (_value: any, _opts: any): React.ReactNode => {
    return this.renderOcrInput()
  }

  private renderOcrInput(): React.ReactNode {
    const props = this.props as IUI2PhotoCaptureFieldProps
    const { ocrValuePath, entity, isDisabled, isReadOnly, placeholder } = props
    const isMobileWeb = this.context === 'mobileWeb'
    const emptyMsg = placeholder || (isMobileWeb ? DEFAULT_EMPTY_MESSAGE_MW : DEFAULT_EMPTY_MESSAGE_DW)
    const currentValue: string = ocrValuePath && entity
      ? _.get(entity.data, ocrValuePath, '')
      : ''
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value
      if (ocrValuePath) {
        ;(props.onChange as any)?.(text, false, ocrValuePath)
      }
    }
    return (
      <input
        type='text'
        className={classNames('ui2-photoCaptureField-ocrInput', {
          'ui2-photoCaptureField-ocrInput--disabled': isDisabled || isReadOnly,
        })}
        value={currentValue}
        placeholder={emptyMsg}
        disabled={!!(isDisabled || isReadOnly)}
        onChange={handleChange}
      />
    )
  }

  // ─── Chip row: square thumbnails (cover fit) ─────────────────────────────
  // Photo captures are visual — render uniform squares instead of the
  // file-upload field's filename text chips.
  protected renderChipList(files: any[]): React.ReactNode {
    const { isDisabled, isReadOnly, addMoreMessage, captureHelpText, multiCaptureHelpText } = this.props as IUI2PhotoCaptureFieldProps
    const editable = !isDisabled && !isReadOnly
    const canRemove = editable
    const atMax = this.isAtMax(files.length)
    const { min } = this.getEffectiveBounds()
    const requiredSlots = editable && files.length < min && !atMax ? min - files.length : 0
    const showOptionalAdd = editable && !atMax && requiredSlots === 0
    return (
      <div className='ui2-photoCaptureField-thumbnailRow'>
        {files.map((file: any, index: number) => {
          const uri = file?.uri || _.get(file, 'source[0].uri') || file?.source?.uri
          const key = file?.uniqueId || `${file?.name || 'photo'}-${index}`
          return (
            <div key={key} className='ui2-photoCaptureField-thumbnail'>
              <button
                type='button'
                className='ui2-photoCaptureField-thumbnailTouchable'
                disabled={!!isDisabled}
                onClick={() => this.handleChipClick(index)}
                aria-label={file?.name || `Photo ${index + 1}`}
              >
                {uri ? (
                  <img src={uri} alt={file?.name || ''} />
                ) : (
                  <span className='ui2-photoCaptureField-thumbnailPlaceholder'>
                    <Icon icon={IconNames.CAMERA} iconSize={20} />
                  </span>
                )}
              </button>
              {canRemove && (
                <button
                  type='button'
                  className='ui2-photoCaptureField-thumbnailRemove'
                  onClick={(e) => {
                    e.stopPropagation()
                    this.handleRemoveAtIndex(index)
                  }}
                  aria-label={`Remove ${file?.name || 'photo'}`}
                >
                  <Icon icon={IconNames.CROSS} iconSize={10} />
                </button>
              )}
            </div>
          )
        })}
        {_.times(requiredSlots, (i) => {
          const label = multiCaptureHelpText?.[files.length + i] ?? captureHelpText ?? DEFAULT_ADD_MORE_TEXT
          return (
            <button
              key={`required-${i}`}
              type='button'
              className='ui2-photoCaptureField-thumbnailAdd ui2-photoCaptureField-thumbnailAdd--dashed'
              onClick={this.handleOpenPicker}
              aria-label={label}
            >
              <Icon icon={IconNames.CAMERA} iconSize={20} />
              <span className='ui2-photoCaptureField-thumbnailAdd-label'>{label}</span>
            </button>
          )
        })}
        {showOptionalAdd && (
          <button
            type='button'
            className='ui2-photoCaptureField-thumbnailAdd'
            onClick={this.handleOpenPicker}
            aria-label={addMoreMessage || DEFAULT_ADD_MORE_TEXT}
          >
            <Icon icon={IconNames.CAMERA} iconSize={20} />
          </button>
        )}
        {this.renderHiddenInput()}
      </div>
    )
  }

  // ─── Empty state: camera icon instead of upload icon ─────────────────────
  // Reuses base class CSS classes — only the icon and button label differ.
  protected renderEmptyStateDesktop(): React.ReactNode {
    const { placeholder, isDisabled } = this.props
    const { isDragging } = this.state
    const message = placeholder || DEFAULT_EMPTY_MESSAGE_DW
    return (
      <>
        <div
          className={classNames('ui2-fileUploadField-dropZone', {
            'ui2-fileUploadField-dropZone--isDragging': isDragging,
            'ui2-fileUploadField-dropZone--isDisabled': isDisabled,
          })}
          onClick={isDisabled ? undefined : this.handleOpenPicker}
          onDragEnter={isDisabled ? undefined : this.handleDragEnter_photo}
          onDragOver={isDisabled ? undefined : this.handleDragOver_photo}
          onDragLeave={isDisabled ? undefined : this.handleDragLeave_photo}
          onDrop={isDisabled ? undefined : this.handleDrop_photo}
        >
          <div className='ui2-fileUploadField-dropZoneMessage'>{message}</div>
          <button
            type='button'
            className='ui2-fileUploadField-dropZoneButton'
            onClick={(e) => {
              e.stopPropagation()
              this.handleOpenPicker()
            }}
            disabled={isDisabled}
          >
            <Icon icon={IconNames.CAMERA} iconSize={14} />
            <span>Upload photo</span>
          </button>
        </div>
        {this.renderHiddenInput()}
      </>
    )
  }

  protected renderEmptyStateMobile(): React.ReactNode {
    const { placeholder, isDisabled } = this.props
    const message = placeholder || DEFAULT_EMPTY_MESSAGE_MW
    return (
      <>
        <div
          role='button'
          tabIndex={isDisabled ? -1 : 0}
          className='ui2-fileUploadField-mobileEmpty'
          onClick={isDisabled ? undefined : this.handleOpenPicker}
          onKeyDown={isDisabled ? undefined : this.handleMobileEmptyKeyDown}
          aria-disabled={isDisabled || undefined}
        >
          <Icon icon={IconNames.CAMERA} iconSize={16} />
          <span className='ui2-fileUploadField-mobileEmptyText'>{message}</span>
        </div>
        {this.renderHiddenInput()}
      </>
    )
  }

  // The base class uses private drag handlers so we redeclare them here.
  private handleDragEnter_photo = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!this.state.isDragging) this.setState({ isDragging: true })
  }

  private handleDragOver_photo = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  }

  private handleDragLeave_photo = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ isDragging: false })
  }

  private handleDrop_photo = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ isDragging: false })
    const dropped = e.dataTransfer?.files
    if (!dropped || dropped.length === 0) return
    this.ingestFiles(dropped)
  }
}
